import React, { useState, useEffect, useRef } from 'react';
import {
    LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
    ResponsiveContainer, Brush, ReferenceLine
} from 'recharts';
import { Button, Slider, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import html2canvas from 'html2canvas';

const MODEL_COLORS = {
    lstm: '#FF5733',     // Red-orange
    bilstm: '#33A1FF',   // Blue
    arima: '#33FF57',    // Green
    sarima: '#D033FF',   // Purple
    iterative: '#FFC300' // Yellow
};

// Custom dot component to handle both black (same values) and colored (different values) dots
const CustomDot = (props) => {
    const { cx, cy, stroke, payload, dataKey, allSameAtPoint } = props;
    // Return null if no data for this point
    if (!payload || payload[dataKey] === undefined) return null;
    return (
        <circle
            cx={cx}
            cy={cy}
            r={4}
            fill={allSameAtPoint ? "#000000" : stroke}
            stroke="none"
        />
    );
};

const ModelComparisonChart = ({ modelsData }) => {
    const [zoomLevel, setZoomLevel] = useState(0);
    const [dataLimit, setDataLimit] = useState(100);
    const [combinedData, setCombinedData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const chartRef = useRef(null);

    const [startIndex, setStartIndex] = useState(0);
    const [endIndex, setEndIndex] = useState(0);

    // Process and combine the data from all models
    useEffect(() => {
        if (!modelsData || !modelsData.length) return;

        // Create a combined dataset with timestamps as keys
        const dataMap = new Map();

        // First, gather all unique timestamps from all models
        modelsData.forEach(modelSet => {
            if (!modelSet.data) return;

            modelSet.data.forEach(point => {
                const timestamp = point.mTm;
                if (!dataMap.has(timestamp)) {
                    dataMap.set(timestamp, {
                        mTm: timestamp,
                        timestamp: new Date(timestamp).getTime() // Add numeric timestamp for sorting
                    });
                }
            });
        });

        // Then fill in the values for each model
        modelsData.forEach(modelSet => {
            const modelName = modelSet.model;
            if (!modelSet.data) return;

            modelSet.data.forEach(point => {
                const timestamp = point.mTm;
                const entry = dataMap.get(timestamp);
                if (entry) {
                    entry[modelName] = point.wtr;
                }
            });
        });

        // Convert to array and sort by timestamp
        let processedData = Array.from(dataMap.values())
            .sort((a, b) => a.timestamp - b.timestamp);

        // Add a flag for each point to indicate if all models have the same value
        processedData = processedData.map(point => {
            // Get all model values that exist for this point
            const values = Object.keys(MODEL_COLORS)
                .map(model => point[model])
                .filter(value => value !== undefined);

            // Check if all values are the same
            const allSame = values.length > 1 && values.every(val => val === values[0]);

            return {
                ...point,
                allSameValues: allSame
            };
        });

        setCombinedData(processedData);
        setEndIndex(processedData.length - 1);
    }, [modelsData]);

    // Replace the existing useEffect for filtered data with this:
    useEffect(() => {
        if (combinedData.length === 0) return;

        // Calculate visible range based on zoom level
        const range = combinedData.length;
        const zoomFactor = zoomLevel / 100;

        // Calculate start and end indices
        const newStart = Math.floor(range * zoomFactor * 0.5);
        const newEnd = Math.ceil(range * (1 - zoomFactor * 0.5)) - 1;

        setStartIndex(Math.max(0, newStart));
        setEndIndex(Math.min(combinedData.length - 1, newEnd));

        // Get the visible data based on zoom indices
        let visibleData = combinedData.slice(
            Math.max(0, newStart),
            Math.min(combinedData.length, newEnd + 1)
        );

        // Apply data limit filter if not set to "All"
        if (dataLimit < visibleData.length) {
            visibleData = visibleData.slice(-dataLimit);
        }

        setFilteredData(visibleData);
    }, [combinedData, dataLimit, zoomLevel]);

    // Handle zoom functions
    const handleZoomIn = () => {
        setZoomLevel(prev => Math.min(prev + 10, 100));
    };

    const handleZoomOut = () => {
        setZoomLevel(prev => Math.max(prev - 10, 0));
    };

    const resetZoom = () => {
        setZoomLevel(0);
        setStartIndex(0);
        setEndIndex(combinedData.length - 1);
    };

    const handleZoomChange = (event, newValue) => {
        setZoomLevel(newValue);
    };

    const handleDataLimitChange = (event) => {
        setDataLimit(parseInt(event.target.value));
    };

    // Calculate the domain/range for the chart based on zoom level
    const calculateDomain = () => {
        if (zoomLevel === 0 || filteredData.length === 0) return undefined;

        // Get the full domain
        const minTime = filteredData[0].timestamp;
        const maxTime = filteredData[filteredData.length - 1].timestamp;
        const timeRange = maxTime - minTime;

        // Calculate zoom window
        const zoomPercent = zoomLevel / 100;
        const zoomWindow = timeRange * (1 - zoomPercent);
        const midPoint = (minTime + maxTime) / 2;

        // Calculate new domain
        const newMin = midPoint - (zoomWindow / 2);
        const newMax = midPoint + (zoomWindow / 2);

        return [new Date(newMin).toISOString(), new Date(newMax).toISOString()];
    };


    const handleDownload = () => {
        if (chartRef.current) {
            const chartContainer = chartRef.current;

            html2canvas(chartContainer, {
                scale: 2, // Higher resolution
                useCORS: true
            }).then(canvas => {
                const imgData = canvas.toDataURL('image/png');
                const link = document.createElement('a');
                link.download = 'model-comparison.png';
                link.href = imgData;
                link.click();
            }).catch(err => {
                console.error("Error generating chart image:", err);
            });
        }
    };

    // Format dates for tooltip and axes
    const formatDate = (date) => {
        if (!date) return '';
        return new Date(date).toLocaleDateString() + ' ' + new Date(date).toLocaleTimeString();
    };

    return (
        <div className="graph-container" style={{ marginTop: '30px' }}>
            <h3>Model Comparison: Water Level (meter) over Time</h3>

            <div className="button-group" style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                <Button variant="contained" onClick={handleZoomIn} className="zoom-button">
                    Zoom In
                </Button>
                <Button variant="contained" onClick={handleZoomOut} className="zoom-button">
                    Zoom Out
                </Button>
                <Button variant="contained" onClick={resetZoom} className="zoom-button">
                    Reset Zoom
                </Button>
                <Button variant="contained" onClick={handleDownload} className="zoom-button">
                    Save Image
                </Button>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: "10px", width: "100%", marginBottom: '15px' }}>
                <Slider
                    value={zoomLevel}
                    onChange={handleZoomChange}
                    aria-labelledby="zoom-slider"
                    min={0}
                    max={100}
                    step={5}
                    valueLabelDisplay="auto"
                    style={{ flex: 1 }}
                />
                <h3 style={{ minWidth: "50px", margin: 0 }}>{zoomLevel}%</h3>
            </div>

            <div className="filter-container" style={{ display: 'flex', alignItems: 'center', marginBottom: '15px' }}>
                <h3 className="filter-label" style={{ marginRight: '10px' }}>Filter Data: </h3>
                <FormControl variant="outlined" size="small" style={{ minWidth: '120px' }}>
                    <InputLabel>Data Points</InputLabel>
                    <Select
                        value={dataLimit}
                        onChange={handleDataLimitChange}
                        label="Data Points"
                    >
                        <MenuItem value={10}>Last 10</MenuItem>
                        <MenuItem value={50}>Last 50</MenuItem>
                        <MenuItem value={100}>Last 100</MenuItem>
                        <MenuItem value={1000}>Last 1000</MenuItem>
                        <MenuItem value={filteredData.length}>All</MenuItem>
                    </Select>
                </FormControl>
            </div>

            <div ref={chartRef} style={{ width: '100%', height: 400 }}>
            <ResponsiveContainer width="100%" height="100%">
                <LineChart
                    data={filteredData}
                    margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                        dataKey="mTm"
                        tickFormatter={(tick) => new Date(tick).toLocaleDateString()}
                        domain={calculateDomain()}
                        type="category"
                        label={{ value: "Measurement Time", position: "insideBottom", offset: 0, dy: 3 }}
                    />
                    <YAxis
                        domain={['auto', 'auto']}
                        label={{ value: "Water Level(meter)", angle: -90, position: "insideLeft", dy: 75 }}
                    />
                        <Tooltip
                            labelFormatter={(value) => `Time: ${formatDate(value)}`}
                            formatter={(value, name) => {
                                // Format the tooltip values
                                return [value !== undefined ? value.toFixed(4) : "N/A", name];
                            }}
                        />
                    <Legend verticalAlign="top" height={36} />


                        {Object.entries(MODEL_COLORS).map(([model, color]) => (
                            <Line
                                key={model}
                                type="monotone"
                                dataKey={model}
                                name={model.toUpperCase()}
                                stroke={color}
                                activeDot={{ r: 8 }}
                                connectNulls={true}
                                dot={(props) => {
                                    if (!props.payload || !props.payload[model]) return null;
                                    return (
                                        <CustomDot
                                            {...props}
                                            allSameAtPoint={props.payload.allSameValues}
                                        />
                                    );
                                }}
                            />
                        ))}

                    <Brush
                        dataKey="mTm"
                        height={30}
                        stroke="#8884d8"
                        y={372}
                        tickFormatter={(tick) => new Date(tick).toLocaleDateString()}
                    />
                </LineChart>
            </ResponsiveContainer>
            </div>

            <div style={{ marginTop: '10px', padding: '10px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                <p style={{ margin: '0px' }}>
                    <strong>Note:</strong> Black dots indicate points where all models predict the same value.
                    Colored dots show where models differ in their predictions.
                </p>
            </div>
        </div>
    );
};

export default ModelComparisonChart;